import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { graphql, useStaticQuery, Link } from "gatsby"
import FormInputMultiCheckbox from "./form-components/form-input-multi-checkbox"
import FormInputRadio from "./form-components/form-input-radio"
import FormInputDropdown from "./form-components/form-input-select"
import FormInputText from "./form-components/form-input-text"
import FormInputTextarea from "./form-components/form-input-textarea"
import FormInputCheckbox from "./form-components/form-input-checkbox"
import FormInputHoneypot from "./form-components/form-input-honeypot"
import Information from "icons/circle-info.svg"
// import Information from "../../../public/icons/circle-info.svg"

export const query = graphql`
  fragment FormFields on DatoCmsForm {
    id
    model {
      apiKey
    }
    title
    componentId: key
    submitButtonText
    formId
    headline
    preamble
    confirmationTitle
    confirmationPreamble
    confirmationAsset {
      url(imgixParams: { dl: "" })
    }
    includeTermsAndConditionsCheckbox
    language
    darkBackground
    fineprintNode {
      childMarkdownRemark {
        html
      }
    }
    formFields {
      ...FormInputText
      ...FormInputTextarea
      ...FormInputCheckbox
      ...FormInputRadio
      ...FormInputDropdown
    }
  }
`

export interface FormProps extends Queries.FormFieldsFragment {
  standalone?: boolean
  center?: boolean
  alternateTitle?: string | null | undefined
  alternatePreamble?: string | null | undefined
}

const Form = (props: FormProps) => {
  const data = useStaticQuery(graphql`
    query TranslationsQuery {
      translations: allLanguagesYaml {
        ...LanguagesData
      }
    }
  `)
  if (!props.formFields) {
    return null
  }

  const { handleSubmit, control, setValue, reset, getValues } = useForm()
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] = useState(false)
  const [hasErrorOnSubmit, setHasErrorOnSubmit] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [translations, setTranslations] = useState<
    Queries.LanguagesYamlCareer[]
  >([])

  const formFields = props.formFields
  const { standalone = true, center = false, darkBackground = false } = props

  const confirmationAsset = props.confirmationAsset?.url
    ? props.confirmationAsset.url
    : false

  const componentId = props.componentId
  const title = props.alternateTitle ? props.alternateTitle : props.headline
  const preamble = props.alternatePreamble
    ? props.alternatePreamble
    : props.preamble
  const fineprint = props.fineprintNode?.childMarkdownRemark?.html || null

  const submitUrl = `https://submit-form.com/${props.formId}`

  const titleClass = `c-heading--b c-form__title ${
    standalone ? "-standalone" : "-with-dash"
  }
    ${!standalone && !preamble ? "-with-margin" : ""}`

  useEffect(() => {
    const selectedLang = !props.language ? "en" : props.language
    const translationNode = data.translations.edges.find(
      e => e.node.parent?.name === selectedLang,
    )
    setTranslations(translationNode.node.form)
  }, [props.language, data.translations])

  const onSubmit = data => {
    fetch(submitUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(data),
    })
      .then(response => {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: "form_submit",
        })
        setIsSuccessfullySubmitted(response.ok)
        if (response.ok && confirmationAsset) {
          window.open(confirmationAsset)
        }
      })
      .catch(() => {
        setIsSuccessfullySubmitted(false)
        setHasErrorOnSubmit(true)
      })
  }

  const handleResetForm = () => {
    reset()
    setHasErrorOnSubmit(false)
    setIsSuccessfullySubmitted(false)
  }

  const renderConfirmation = () => {
    return (
      <>
        {props.confirmationTitle && (
          <h2 className={titleClass}>{props.confirmationTitle}</h2>
        )}
        {props.confirmationPreamble && (
          <div className="c-modal__preamble">{props.confirmationPreamble}</div>
        )}
      </>
    )
  }

  const renderConfirmationAssetInfo = () => {
    return (
      <>
        {confirmationAsset && (
          <div className="c-modal__preamble">
            Did your download not start?{" "}
            <a className="c-link" href={confirmationAsset}>
              Click here
            </a>
          </div>
        )}
      </>
    )
  }

  const renderError = () => {
    return (
      <>
        <h2 className={titleClass}>Something went wrong</h2>
        <div className="c-modal__preamble">
          <span>Please try again or reach out to us at </span>
          <a className="c-link" href="mailto:hi@northvolt.com">
            hi@northvolt.com
          </a>
        </div>
        <button
          type="button"
          onClick={handleResetForm}
          className="c-cta -green"
        >
          Reset form and try again
        </button>
      </>
    )
  }

  const renderFormIntroText = () => {
    return (
      <>
        {title && <h2 className={titleClass}>{title}</h2>}
        {preamble && <div className="c-modal__preamble">{preamble}</div>}
      </>
    )
  }

  const form = (
    <>
      {renderFormIntroText()}
      <form onSubmit={handleSubmit(onSubmit)}>
        {formFields?.map(block => {
          return (
            <React.Fragment key={block.id}>
              {(() => {
                switch (block.model.apiKey) {
                  case "form_input_text":
                    return (
                      <FormInputText
                        {...(block as Queries.FormInputTextFragment)}
                        control={control}
                      />
                    )
                  case "form_input_textarea":
                    return (
                      <FormInputTextarea
                        {...(block as Queries.FormInputTextareaFragment)}
                        control={control}
                      />
                    )
                  case "form_input_checkbox":
                    return (
                      <FormInputMultiCheckbox
                        {...(block as Queries.FormInputCheckboxFragment)}
                        control={control}
                        getValues={getValues}
                        setValue={setValue}
                        columns={standalone ? 3 : 2}
                      />
                    )
                  case "form_input_radio":
                    return (
                      <FormInputRadio
                        {...(block as Queries.FormInputRadioFragment)}
                        control={control}
                        columns={standalone ? 3 : 2}
                      />
                    )
                  case "form_input_dropdown":
                    return (
                      <FormInputDropdown
                        {...(block as Queries.FormInputDropdownFragment)}
                        control={control}
                      />
                    )
                }
              })()}
            </React.Fragment>
          )
        })}
        {props.includeTermsAndConditionsCheckbox && (
          <FormInputCheckbox
            name="agreement"
            control={control}
            label="I agree to the <a target='_blank' href='/articles/privacy'>terms and conditions</a>"
            value="i-agree"
            required={true}
            small={true}
          />
        )}
        <FormInputHoneypot />
        {fineprint && (
          <div
            className="c-form__fineprint"
            dangerouslySetInnerHTML={{ __html: fineprint }}
          />
        )}

        <div className="c-form__fineprint -information">
          <Information className="c-form__icon" />
          {(() => {
            const translation =
              translations?.find(
                t => t.key === "form__information_personal_data",
              )?.value ?? "Information on how we process your personal data."

            const [beforeLink, afterLink] = translation.split(
              "your personal data.",
            )

            return (
              <span>
                {beforeLink}
                <Link to="/articles/privacy/">your personal data.</Link>
                {afterLink}
              </span>
            )
          })()}
        </div>

        <input
          type="submit"
          value={props.submitButtonText}
          className="c-cta -green"
        />
      </form>
    </>
  )

  const wrapperClasses = `o-form c-form ${
    standalone
      ? center
        ? `-standalone ${
            darkBackground ? "" : "o-wrapper"
          } u-flex u-flex-column u-vertical-center`
        : // } u-flex u-flex-column u-vertical-center` /
          // This brings back the row margin and replaces the line below
          `-standalone ${darkBackground ? "" : "o-wrapper"} o-row`
      : ""
  } ${darkBackground ? "-dark-background" : ""}`

  return (
    <div id={componentId} className={wrapperClasses}>
      {isSuccessfullySubmitted && !hasErrorOnSubmit && renderConfirmation()}
      {isSuccessfullySubmitted &&
        !hasErrorOnSubmit &&
        confirmationAsset &&
        renderConfirmationAssetInfo()}
      {hasErrorOnSubmit && !isSuccessfullySubmitted && renderError()}
      {isSuccessfullySubmitted || hasErrorOnSubmit ? null : form}
    </div>
  )
}

export default Form
